export interface SseFrame {
  event: string;
  data: unknown;
}

export async function consumeSse(
  response: Response,
  onFrame: (frame: SseFrame) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (!response.body) throw new Error("The server returned an empty event stream.");
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const abort = () => {
    void reader.cancel().catch(() => undefined);
  };
  signal?.addEventListener("abort", abort, { once: true });

  try {
    while (true) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      let boundary = buffer.indexOf("\n\n");
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        let event = "message";
        const dataLines: string[] = [];
        for (const line of block.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
        }
        const raw = dataLines.join("\n");
        if (raw === "[DONE]") return;
        if (raw) {
          let data: unknown = raw;
          try {
            data = JSON.parse(raw);
          } catch {
            // Text-only SSE payloads are valid.
          }
          if (event === "error") {
            const error = data as { message?: string; error?: string };
            throw new Error(error.message ?? error.error ?? "FlowOps stream failed.");
          }
          onFrame({ event, data });
        }
        boundary = buffer.indexOf("\n\n");
      }
    }

    // Flush a final incomplete SSE block when the socket closes cleanly.
    if (buffer.trim()) {
      let event = "message";
      const dataLines: string[] = [];
      for (const line of buffer.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
      }
      const raw = dataLines.join("\n");
      if (raw && raw !== "[DONE]") {
        let data: unknown = raw;
        try {
          data = JSON.parse(raw);
        } catch {
          // Text-only SSE payloads are valid.
        }
        // Same as the main loop: error frames must throw so Retry can surface.
        if (event === "error") {
          const error = data as { message?: string; error?: string };
          throw new Error(error.message ?? error.error ?? "FlowOps stream failed.");
        }
        onFrame({ event, data });
      }
    }
  } finally {
    signal?.removeEventListener("abort", abort);
  }
}

export async function readResponseError(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as {
    error?: string;
    message?: string;
    hint?: string;
  } | null;
  // FlowOps runWorkflowStream: body.error ?? "Request failed" — prefer API detail
  // over raw "500 Internal Server Error".
  const detail =
    body?.error?.trim() ||
    body?.message?.trim() ||
    (response.status >= 500 ? "Request failed" : `${response.status} ${response.statusText}`);
  return body?.hint ? `${detail} ${body.hint}` : detail;
}

export function frameText(data: unknown): string {
  if (typeof data === "string") return data;
  if (!data || typeof data !== "object") return "";
  const value = data as Record<string, unknown>;
  return String(value.text ?? value.message ?? value.summary ?? value.status ?? "");
}
